import type { CmmdClaudeRuntimeReadiness } from "@cmmd-forge/contracts";

import { CmmdApiError } from "../environments/cmmdApi/transport";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";

/**
 * The Claude readiness seam: which id a readiness ask carries, how the ask is
 * sent, and how CMMD's answer is decoded into something a turn can trust.
 *
 * The id question is decided here and nowhere else (FOR-3384). A legacy cloud
 * environment id encodes its runtime id, and that is what CMMD's readiness
 * route has always answered for. A durable `env_` id names no runtime, so the
 * in-thread composer used to pass `null` instead and the picker withheld
 * Claude as "still being applied" forever while /new, reading the same
 * Environment through its catalog runtime id, offered it. The durable id is
 * now sent as itself: CMMD resolves the Environment to whichever runtime
 * currently backs it, so a repair that rotates the runtime cannot strand the
 * answer on a stale id. Until the CMMD half of FOR-3384 teaches the route to
 * answer `env_` ids, such an ask fails and decodes to "no readiness", the same
 * honest state as not asking, with the bounded poll capping the wasted reads.
 * Any other shape names no cloud target at all, and null keeps the no-ask
 * behavior. Consumers with an environment id in hand pass it through
 * `useForgeProviderAccess({ environmentId })`; the legacy runtime id stays as
 * the direct answer for callers that already hold one.
 */
export function resolveClaudeReadinessTargetId(environmentId: unknown): string | null {
  const classification = classifyEnvironmentId(environmentId);
  if (classification.legacyRuntimeId !== null) return classification.legacyRuntimeId;
  return classification.shape === "durable-environment" && typeof environmentId === "string"
    ? environmentId
    : null;
}

/** The readiness read refused on the session's authority, not the runtime's state. */
export class ForgeProviderAccessAuthorityError extends Error {
  constructor(kind: "auth" | "permission") {
    super(
      kind === "auth"
        ? "Your CMMD session expired. Sign in again to verify Claude runtime readiness."
        : "CMMD denied access to verify Claude runtime readiness. Check your organization permissions.",
    );
    this.name = "ForgeProviderAccessAuthorityError";
  }
}

export async function resolveClaudeRuntimeReadiness(input: {
  readonly enabled: boolean;
  readonly readinessTargetId: string | null;
  readonly load: (askedId: string) => Promise<unknown>;
}): Promise<CmmdClaudeRuntimeReadiness | null> {
  if (!input.enabled || input.readinessTargetId === null) return null;
  try {
    return decodeClaudeRuntimeReadiness(
      await input.load(input.readinessTargetId),
      input.readinessTargetId,
    );
  } catch (error) {
    if (error instanceof CmmdApiError && (error.kind === "auth" || error.kind === "permission")) {
      throw new ForgeProviderAccessAuthorityError(error.kind);
    }
    return null;
  }
}

const CLAUDE_DELIVERY_STATUSES = new Set([
  "not_connected",
  "pending",
  "claimed",
  "activating",
  "applied",
  "failed",
  "stale",
]);
const CLAUDE_READINESS_NULLABLE_STRING_FIELDS = [
  "generationId",
  "nativeVerifiedAt",
  "reason",
  "lastErrorCode",
  "lastErrorMessage",
] as const;

/**
 * `askedId` is the id the readiness request named, whatever its shape: the
 * receipt envelope must bind to exactly that id, so an answer cannot be replayed
 * for a different target.
 */
function decodeClaudeRuntimeReadiness(value: unknown, askedId: string): CmmdClaudeRuntimeReadiness {
  if (
    !isRecord(value) ||
    !hasValidClaudeRuntimeReadinessFields(value) ||
    !hasValidClaudeDeliveryReceipt(value, askedId)
  ) {
    throw new Error("CMMD returned invalid Claude runtime readiness.");
  }
  return value as CmmdClaudeRuntimeReadiness;
}

function hasValidClaudeDeliveryReceipt(value: Record<string, unknown>, askedId: string): boolean {
  // An answer that allows a turn must carry its receipt, whatever the status (FOR-3085).
  if (value.deliveryReceipt === null) return value.nativeExecutable !== true;
  if (!isRecord(value.deliveryReceipt)) return false;
  const receipt = value.deliveryReceipt;
  const identity = isRecord(receipt.identity) ? receipt.identity : null;
  if (identity === null) return false;
  return (
    hasValidClaudeReceiptEnvelope(receipt, askedId) &&
    hasMatchingClaudeReceiptIdentity(identity, value.generationId)
  );
}

function hasValidClaudeReceiptEnvelope(receipt: Record<string, unknown>, askedId: string): boolean {
  return hasValidReceiptAuthority(receipt, askedId) && hasValidReceiptTiming(receipt);
}

function hasValidReceiptAuthority(receipt: Record<string, unknown>, askedId: string): boolean {
  return (
    typeof receipt.receiptId === "string" &&
    receipt.receiptId.trim().length > 0 &&
    receipt.runtimeId === askedId &&
    typeof receipt.machineId === "string" &&
    receipt.machineId.trim().length > 0 &&
    typeof receipt.credentialEpoch === "number" &&
    Number.isSafeInteger(receipt.credentialEpoch) &&
    receipt.credentialEpoch >= 0
  );
}

function hasValidReceiptTiming(receipt: Record<string, unknown>): boolean {
  return (
    typeof receipt.issuedAt === "string" &&
    Number.isFinite(Date.parse(receipt.issuedAt)) &&
    receipt.expiresAt === null
  );
}

function hasMatchingClaudeReceiptIdentity(
  identity: Record<string, unknown>,
  generationId: unknown,
): boolean {
  return (
    identity.kind === "credential_generation" &&
    identity.provider === "claude" &&
    typeof identity.credentialGenerationId === "string" &&
    identity.credentialGenerationId === generationId
  );
}

function hasValidClaudeRuntimeReadinessFields(value: Record<string, unknown>): boolean {
  return (
    typeof value.connected === "boolean" &&
    CLAUDE_DELIVERY_STATUSES.has(String(value.deliveryStatus)) &&
    typeof value.nativeExecutable === "boolean" &&
    CLAUDE_READINESS_NULLABLE_STRING_FIELDS.every((field) => isNullableString(value[field]))
  );
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
